(() => {
  'use strict';
  let apiPromise;
  const youtube = () => {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (apiPromise) return apiPromise;
    apiPromise = new Promise((resolve, reject) => {
      const previous = window.onYouTubeIframeAPIReady;
      const timer = setTimeout(() => reject(new Error('YouTube unavailable')), 15000);
      window.onYouTubeIframeAPIReady = () => {
        clearTimeout(timer);
        resolve(window.YT);
        if (typeof previous === 'function') previous();
      };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.onerror = () => { clearTimeout(timer); reject(new Error('YouTube unavailable')); };
        document.head.append(script);
      }
    });
    return apiPromise;
  };
  const init = root => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const track = root.querySelector('[data-video-track]');
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const entries = Array.from(root.querySelectorAll('[data-video-card]')).map(card => ({
      card, id: card.dataset.videoId, player: null, ready: false, ratio: 0, loading: false,
      paused: false, systemPause: false, failed: false, manual: false
    }));
    let auto = !mediaQuery.matches && !navigator.connection?.saveData;
    let active = null;
    let destroyed = false;
    // Read current geometry when users select a card: observer records can lag
    // behind a horizontal scroll and otherwise replace their choice with autoplay.
    const visibleRatio = entry => {
      const card = entry.card.getBoundingClientRect();
      const rail = track.getBoundingClientRect();
      const width = Math.max(0, Math.min(card.right, rail.right, innerWidth) - Math.max(card.left, rail.left, 0));
      const height = Math.max(0, Math.min(card.bottom, rail.bottom, innerHeight) - Math.max(card.top, rail.top, 0));
      return card.width && card.height ? width * height / (card.width * card.height) : 0;
    };
    const autoButton = root.querySelector('[data-video-auto]');
    const refreshAuto = () => {
      autoButton.setAttribute('aria-pressed', String(auto));
      autoButton.textContent = auto ? 'Pausar reproducción automática' : 'Activar reproducción automática';
    };
    const pause = entry => {
      if (entry.ready && [1, 3].includes(entry.player.getPlayerState())) {
        entry.systemPause = true;
        entry.player.pauseVideo();
      }
      entry.card.dataset.playing = 'false';
    };
    const soundLabel = entry => {
      const sound = entry.card.querySelector('[data-video-sound]');
      const muted = !entry.ready || entry.player.isMuted();
      sound.textContent = muted ? 'Activar sonido' : 'Silenciar';
      sound.setAttribute('aria-pressed', String(!muted));
      sound.setAttribute('aria-label', `${muted ? 'Activar sonido de' : 'Silenciar'} ${entry.card.dataset.videoTitle}`);
    };
    const fail = entry => {
      entry.failed = true;
      entry.card.dataset.playing = 'false';
      entry.card.querySelector('[data-video-error]').hidden = false;
      entry.card.querySelector('[data-video-poster]').hidden = false;
      entry.card.querySelector('[data-video-controls]').hidden = true;
    };
    // Player state codes are shared by the YouTube API and the native adapter.
    const stateChange = (entry, state) => {
      const playing = state === 1;
      entry.card.dataset.playing = String(playing);
      entry.card.querySelector('[data-play-label]').textContent = playing ? 'Pausar' : 'Reproducir';
      entry.card.querySelector('[data-video-toggle]').setAttribute('aria-label', `${playing ? 'Pausar' : 'Reproducir'} video de ${entry.card.dataset.videoTitle}`);
      if (playing) {
        if (document.hidden || entry.ratio < .3) { pause(entry); return; }
        entries.filter(other => other !== entry).forEach(pause);
        active = entry;
        entry.paused = false;
        entry.systemPause = false;
        entry.card.querySelector('[data-video-error]').hidden = true;
      } else if (state === 2) {
        if (!entry.systemPause) entry.paused = true;
        entry.systemPause = false;
      } else if (state === 0 && active === entry && auto && entry.ratio >= .5) {
        const visible = entries.filter(item => item.ratio >= .5 && !item.failed && !item.paused);
        const following = visible[(visible.indexOf(entry) + 1) % visible.length];
        if (following && following !== entry) play(following);
        else { entry.player.seekTo(0, true); entry.player.playVideo(); }
      }
      soundLabel(entry);
    };
    const autoplayBlocked = entry => {
      entry.paused = true;
      const notice = entry.card.querySelector('[data-video-error]');
      notice.textContent = 'Toca Reproducir para ver el video.';
      notice.hidden = false;
    };
    const loadNative = entry => {
      const video = entry.card.querySelector('[data-video-native]');
      if (!video) return false;
      entry.player = {
        getPlayerState: () => video.ended ? 0 : video.paused ? 2 : video.readyState < 3 ? 3 : 1,
        isMuted: () => video.muted,
        mute: () => { video.muted = true; },
        unMute: () => { video.muted = false; },
        setVolume: volume => { video.volume = volume / 100; },
        pauseVideo: () => video.pause(),
        seekTo: time => { video.currentTime = time; },
        playVideo: () => {
          video.play().catch(error => {
            if (destroyed || error.name === 'AbortError') return;
            if (error.name === 'NotAllowedError') autoplayBlocked(entry);
            else fail(entry);
          });
        },
        destroy: () => { video.pause(); video.removeAttribute('src'); video.load(); }
      };
      video.addEventListener('playing', () => { if (!destroyed) stateChange(entry, 1); });
      video.addEventListener('pause', () => { if (!destroyed && !video.ended) stateChange(entry, 2); });
      video.addEventListener('ended', () => { if (!destroyed) stateChange(entry, 0); });
      video.addEventListener('volumechange', () => { if (!destroyed) soundLabel(entry); });
      video.addEventListener('error', () => { if (!destroyed) fail(entry); });
      video.src = video.dataset.src;
      entry.ready = true;
      entry.card.querySelector('[data-video-sound]').disabled = false;
      entry.card.querySelector('[data-video-poster]').hidden = true;
      if (active === entry && !document.hidden && (auto || entry.manual)) entry.player.playVideo();
      return true;
    };
    const load = async entry => {
      if (entry.loading || entry.player || entry.failed) return;
      entry.loading = true;
      if (loadNative(entry)) return;
      try {
        const YT = await youtube();
        if (destroyed) return;
        const target = entry.card.querySelector('[data-video-player]');
        entry.player = new YT.Player(target, {
          host: 'https://www.youtube-nocookie.com', videoId: entry.id,
          playerVars: { playsinline: 1, controls: 1, rel: 0, origin: location.origin },
          events: {
            onReady: () => {
              if (destroyed) return;
              entry.ready = true;
              const iframe = entry.player.getIframe();
              iframe.title = `Video de ${entry.card.dataset.videoTitle}`;
              iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
              entry.player.mute();
              entry.card.querySelector('[data-video-sound]').disabled = false;
              entry.card.querySelector('[data-video-poster]').hidden = true;
              if (active === entry && !document.hidden && (auto || entry.manual)) entry.player.playVideo();
            },
            onStateChange: event => stateChange(entry, event.data),
            onError: () => fail(entry),
            onAutoplayBlocked: () => autoplayBlocked(entry)
          }
        });
      } catch (_) { if (!destroyed) fail(entry); }
    };
    const play = (entry, manual = false) => {
      if (entry.failed || document.hidden) return;
      entry.ratio = visibleRatio(entry);
      entries.filter(other => other !== entry).forEach(pause);
      active = entry;
      entry.manual = manual;
      entry.paused = false;
      if (entry.ready) {
        if (!manual) { entry.player.mute(); soundLabel(entry); }
        entry.player.playVideo();
      }
      else load(entry);
    };
    const reconcile = () => {
      entries.filter(entry => entry.ratio < .3 || document.hidden).forEach(pause);
      if (document.hidden) return;
      if (active && active.ratio >= (active.manual ? .3 : .5) && !active.failed) {
        if ((auto || active.manual) && !active.paused && (!active.ready || ![1, 3].includes(active.player.getPlayerState()))) play(active, active.manual);
        return;
      }
      if (active) { pause(active); active.manual = false; active = null; }
      if (!auto) return;
      const next = entries.find(entry => entry.ratio >= .5 && !entry.paused && !entry.failed);
      if (next) play(next);
    };
    const observer = new IntersectionObserver(() => {
      entries.forEach(entry => { entry.ratio = visibleRatio(entry); });
      reconcile();
    }, { threshold: [0, .3, .5, .75, 1] });
    entries.forEach(entry => {
      observer.observe(entry.card);
      entry.card.querySelector('[data-video-controls]').hidden = false;
      entry.card.querySelector('[data-video-poster]').addEventListener('click', event => {
        if (entry.failed) return;
        event.preventDefault();
        play(entry, true);
      });
      entry.card.querySelector('[data-video-toggle]').addEventListener('click', () => {
        if (entry.ready && entry.player.getPlayerState() === 1) {
          entry.paused = true; entry.manual = false; pause(entry);
        } else play(entry, true);
      });
      entry.card.querySelector('[data-video-sound]').addEventListener('click', () => {
        if (!entry.ready) { play(entry, true); return; }
        const muted = entry.player.isMuted();
        if (muted) { play(entry, true); entry.player.unMute(); entry.player.setVolume(70); }
        else entry.player.mute();
        soundLabel(entry);
      });
    });
    autoButton.hidden = false;
    refreshAuto();
    autoButton.addEventListener('click', () => {
      auto = !auto;
      entries.forEach(entry => { entry.manual = false; if (auto) entry.paused = false; else pause(entry); });
      refreshAuto(); reconcile();
    });
    const previous = root.querySelector('[data-video-prev]');
    const next = root.querySelector('[data-video-next]');
    root.querySelector('[data-video-navigation]').hidden = false;
    const updateArrows = () => {
      previous.disabled = track.scrollLeft < 5;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 5;
    };
    const move = direction => track.scrollBy({ left: direction * (entries[0].card.offsetWidth + parseFloat(getComputedStyle(track).gap)), behavior: mediaQuery.matches ? 'instant' : 'smooth' });
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', updateArrows, { passive: true });
    const resize = new ResizeObserver(updateArrows); resize.observe(track); updateArrows();
    const onVisibility = () => reconcile();
    document.addEventListener('visibilitychange', onVisibility);
    const onMotion = () => { if (mediaQuery.matches) { auto = false; entries.forEach(entry => { entry.manual = false; pause(entry); }); refreshAuto(); } };
    mediaQuery.addEventListener('change', onMotion);
    root.destroyVideoRail = () => {
      destroyed = true; observer.disconnect(); resize.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      mediaQuery.removeEventListener('change', onMotion);
      entries.forEach(entry => entry.player?.destroy());
    };
  };
  const scan = scope => scope.querySelectorAll('[data-robot-videos]').forEach(init);
  scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-robot-videos]').forEach(root => root.destroyVideoRail?.()));
})();
